import { Button, Grid } from '@mui/material';
import { SiTrackGrid } from '../../../components/si-track-grid/si-track-grid';
import { SaveHookFormDialog } from '../../../components/save-hook-form.dialog';
import { useState } from 'react';
import { Service } from '../../../models/masters/service';
import AddIcon from '@mui/icons-material/Add';

import { columns } from './grid-columns';
import { ServiceForm } from './service.form';
import { serviceFormSchema } from './service-form.schema';
import { servicesApiKeyStore } from '../../../api/masters/services-api-key-store';
import { FormInput } from '../../../components/hook-form/form-input.component';
import { ServicesWideCutoffTime } from './service-wide-cutoff-time';
import { useLoadData } from '../../../hooks/useLoadData';
import { ApiResponse } from '../../../models/api-response';
import { server } from 'typescript';
import { useSaveData } from '../../../hooks/useSaveData';
import { UserPermissionName } from '../../../models/user-permission-name';
import { GuardedComponent } from '../../../components/guarded-component';
import { useAuthorize } from '../../../hooks/use-authorize';
import { useSnackbar } from '../../../hooks/use-snackbar.hook';

interface ChangedService {
  id: number;
  changes: Record<string, { before: any; after: any }>;
}

const DEFAULT_GROUP = [
  {
    field: 'serviceGroupName',
  },
];

export const ServiceTab = () => {
  const { showSnackbar } = useSnackbar();
  const [isEdit, setisEdit] = useState<boolean>(false);
  const [editItem, setEditItem] = useState<Partial<Service>>();
  const { data, refetch, isLoading } = useLoadData<ApiResponse<Service[]>>(servicesApiKeyStore.getServices());
  const { isLoading: isLoadingSave, mutateAsync } = useSaveData<any>(servicesApiKeyStore.updateServiceSortOrder());

  const handleEditService = (service: Service) => {
    setisEdit(true);
    setEditItem({ ...service, isPlanReviewRequired: !!service.isPlanReviewRequired });
  };

  const handleEditClose = () => {
    setisEdit(false);
    setEditItem(undefined);
  };

  const handleCreateService = () => {
    setisEdit(false);
    setEditItem({ isActive: true, isPlanReviewRequired: false });
  };

  const formatSubmitdata = (data: any) => {
    const newData = {
      ...data,
      fastFieldFormID: data.fastFieldFormID === '' ? 0 : parseInt(data.fastFieldFormID),
      price: data.price,
    };
    return {
      //...data,
      ...newData,
      //servicePredecessors: [data.servicePredecessors],
      oneStoryServicePredecessors: data.oneStoryServicePredecessors?.map((item: any) => item.serviceID),
      twoStoryServicePredecessors: data.twoStoryServicePredecessors?.map((item: any) => item.serviceID),
      serviceEquipments: data.serviceEquipments?.map((item: any) => item.equipmentID),
      serviceMarketAreas: data.serviceMarketAreas?.map((item: any) => item.marketAreaID),
      serviceSkillsets: data.serviceSkillsets?.map((item: any) => item.skillsetID),
      userManagers: data.userManagers?.map((item: any) => item.userID),
      isPlanReviewRequired: data.isPlanReviewRequired === 'true' || data.isPlanReviewRequired === true ? true : false,
      isPassFailedApplicable:
        data.isPassFailedApplicable === 'true' || data.isPassFailedApplicable === true ? true : false,
    };
  };

  const findChangedObjects = (array1: Service[], array2: Service[]): ChangedService[] => {
    const array2Map = new Map(array2.map((obj) => [obj.serviceID, obj]));

    const changedObjects: ChangedService[] = [];

    Array.isArray(array1) &&
      array1?.forEach((obj1) => {
        const obj2 = array2Map.get(obj1.serviceID);

        if (!obj2) {
          changedObjects.push({
            id: obj1.serviceID,
            changes: {},
          });
          return;
        }

        const changes: Record<string, { before: any; after: any }> = {};
        Array.isArray(Object.keys(obj1)) &&
          Object.keys(obj1)?.forEach((key) => {
            if (obj1[key] !== obj2[key]) {
              changes[key] = { before: obj1[key], after: obj2[key] };
            }
          });

        if (Object.keys(changes).length > 0) {
          changedObjects.push({
            id: obj1.serviceID,
            changes,
          });
        }
      });

    return changedObjects;
  };

  const handleOnChange = async (value: any[]) => {
    if (data?.data && value?.length > 0) {
      const changed = findChangedObjects(data?.data, value);
      if (changed?.length > 0) {
        const sortOrderValue = !changed[0]?.changes?.sortOrder?.after ? '0' : changed[0]?.changes?.sortOrder?.after;
        const submitingData = {
          serviceID: changed[0]?.id,
          sortOrder: Number(sortOrderValue),
        };
        if (!isReadOnlyPermission) {
          await mutateAsync({
            data: submitingData,
          });
          refetch();
        }
      }
    }
  };

  const handleSubmit = (data: any) => {
    if (isEdit) {
      if (data?.syncWithDux || data?.isDuxService || data?.allowToScheduleFromDux || data?.customerReportViewDux)
        showSnackbar(`Please note that changes to DUX-related flags will be reflected in DUX only after the projects containing this service are updated in SI Track.`, 'warning');
    }
  }

  const isReadOnlyPermission = useAuthorize([UserPermissionName?.READ_ONLY]);

  return (
    <>
      <Grid container direction="row" justifyContent="space-between" alignItems="center" style={{ paddingBottom: 10 }}>
        <ServicesWideCutoffTime />
        <GuardedComponent requiredPermissions={[UserPermissionName.ADD_SERVICE]}>
          <Button
            style={{ marginLeft: 'auto', marginTop: '-8px', marginBottom: '8px' }}
            size="small"
            variant="contained"
            disableElevation={true}
            startIcon={<AddIcon />}
            onClick={handleCreateService}
          >
            Add Service
          </Button>
        </GuardedComponent>
      </Grid>
      <div className="form-group">
        <SiTrackGrid
          fileTitle='service'
          dataItemKey="serviceID"
          columns={columns}
          onEdit={handleEditService}
          // deleteMutationConfig={(deleteItem) => servicesApiKeyStore.deleteService(deleteItem.serviceID)}
          onGridDataChange={handleOnChange}
          dataItems={data?.data}
          isBusy={isLoading || isLoadingSave}
          // defaultGroup={DEFAULT_GROUP}
          actionPermissions={{
            edit: UserPermissionName.EDIT_SERVICE,
            export: UserPermissionName.DOWNLOAD_GRID,
          }}
        />
      </div>
      {editItem && (
        <SaveHookFormDialog
          title={editItem.serviceID ? 'UPDATE SERVICE' : 'CREATE SERVICE'}
          config={{
            saveConfig: editItem.serviceID ? servicesApiKeyStore.updateService() : servicesApiKeyStore.createService(),
            invalidateRoute: () => servicesApiKeyStore.getServices(),
          }}
          formatSubmitdata={formatSubmitdata}
          data={editItem}
          yupSchema={serviceFormSchema}
          // onSaveCompleted={handleSubmit}
          onClose={handleEditClose}
          size="md"
        >
          <ServiceForm />
        </SaveHookFormDialog>
      )}
    </>
  );
};
